import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check, Plus } from "lucide-react";
import { Input } from "../ui/input";

// A text input that also offers the values already in use, so the usual
// ones can be picked instead of re-typed (and mis-typed) — while still
// accepting anything new.
//
// Deliberately NOT built on Popover/Command: every place this is used
// sits inside a Dialog, and a portalled popover fights the dialog's
// focus trap. This renders the list inline and absolutely positioned,
// so there's nothing to conflict with.
//
// It stays an <Input> at heart: typing always works and never needs an
// extra click, which is the point — the list is an aid, not a gate.
export const SuggestInput = ({
  value,
  onChange,
  suggestions = [],
  placeholder,
  className = "",
  testId,
  emptyHint = "No saved values yet — type to add the first one",
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  // Normalise: callers may pass plain strings or {name, count} rows.
  const options = useMemo(
    () =>
      (suggestions || [])
        .map(s => (typeof s === "string" ? { name: s } : s))
        .filter(s => s && String(s.name || "").trim() !== ""),
    [suggestions]
  );

  const typed = String(value ?? "");
  const filtered = useMemo(() => {
    const q = typed.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.name.toLowerCase().includes(q));
  }, [options, typed]);

  // An exact match means the typed value is already a known one — no
  // need to advertise it as new.
  const isNewValue =
    typed.trim() !== "" &&
    !options.some(o => o.name.toLowerCase() === typed.trim().toLowerCase());

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Escape must close ONLY the suggestion list, never the surrounding
  // Dialog (that would throw away a half-filled form).
  //
  // Radix's Dialog registers its Escape handler on `document` in the
  // CAPTURE phase, so it fires before any React bubble handler and
  // before a capture listener on our own element. A capture listener on
  // `window` is the one place that runs EARLIER (window → document →
  // … → target), so that's where we intercept and stop it.
  useEffect(() => {
    if (!open) return undefined;
    const onEscapeCapture = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setOpen(false);
      setHighlight(-1);
    };
    window.addEventListener("keydown", onEscapeCapture, true);
    return () => window.removeEventListener("keydown", onEscapeCapture, true);
  }, [open]);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
    setHighlight(-1);
  };

  // Handled on the WRAPPER, not the input: the chevron toggle can hold
  // focus too, and if Escape isn't swallowed here it reaches the
  // surrounding Dialog and closes the whole modal — losing the form.
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, -1));
    } else if (e.key === "Enter") {
      if (open && highlight >= 0 && filtered[highlight]) {
        e.preventDefault();
        pick(filtered[highlight].name);
      }
    } else if (e.key === "Escape") {
      // Handled by the window-capture listener below — Radix's Dialog
      // listens for Escape in the CAPTURE phase on `document`, which
      // runs before anything in React's bubble phase.
    }
  };

  return (
    <div className="relative" ref={wrapRef} onKeyDown={onKeyDown}>
      <Input
        value={typed}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        // Opens on a real interaction (click, typing, chevron) rather
        // than on focus: these fields are often the first in a Dialog,
        // and Radix autofocuses them — a focus-triggered list would pop
        // open unbidden every time the modal was opened.
        onClick={() => setOpen(true)}
        placeholder={placeholder}
        className={`pr-8 ${className}`}
        autoComplete="off"
        data-testid={testId}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setOpen(o => !o)}
        onFocus={() => { /* keep the list state; focus alone shouldn't open it */ }}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
        aria-label="Show saved values"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-slate-900 shadow-xl max-h-52 overflow-y-auto"
          data-testid={testId ? `${testId}-options` : undefined}
        >
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-500">
              {options.length === 0 ? emptyHint : `No saved value matches "${typed.trim()}"`}
            </p>
          )}
          {filtered.map((o, i) => {
            const selected = o.name.toLowerCase() === typed.trim().toLowerCase();
            return (
              <button
                key={o.name}
                type="button"
                onClick={() => pick(o.name)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors ${
                  i === highlight ? "bg-slate-800 text-white" : "text-slate-200 hover:bg-slate-800"
                }`}
                data-testid={testId ? `${testId}-option-${o.name}` : undefined}
              >
                <span className="flex items-center gap-2 truncate">
                  {selected && <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                  <span className="truncate">{o.name}</span>
                </span>
                {typeof o.count === "number" && (
                  <span className="text-xs text-slate-500 shrink-0">
                    {o.count}&times;
                  </span>
                )}
              </button>
            );
          })}
          {isNewValue && (
            <div
              className="px-3 py-2 text-xs text-amber-400/90 border-t border-slate-800 flex items-center gap-1.5"
              data-testid={testId ? `${testId}-new-value-hint` : undefined}
            >
              <Plus className="w-3 h-3 shrink-0" />
              Will be saved as a new value: &ldquo;{typed.trim()}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestInput;
